import { useState } from "react";
import {
  X,
  ClipboardList,
  Loader2,
} from "lucide-react";

import {
  createWorkspaceItem,
} from "../services/workspaceService";

const initialForm = {
  title: "",
  description: "",
  estimated_hours: "",
  priority: "Medium",
  sort_order: 1,
  status: "Active",
};

const AssignTaskDrawer = ({
  open,
  onClose,
  workspace,
  project,
  onCreated,
}) => {
  const [formData, setFormData] =
    useState(initialForm);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  if (!open) return null;

  /**
   * ============================================================
   * HANDLE CHANGE
   * ============================================================
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * ============================================================
   * SUBMIT
   * ============================================================
   */
  const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    setLoading(true);
    setError("");

    const workspaceUuid =
      workspace?.uuid ||
      workspace?.workspace_uuid ||
      workspace?.id;

    if (!workspaceUuid) {
      setError("Workspace UUID is missing.");
      return;
    }

    const payload = {
      // your item fields
      // example:
      title: formData.title,
      description: formData.description,
      estimated_hours: Number(
        formData.estimated_hours
      ),
      priority: formData.priority,
      status: formData.status,
    };

    console.log(
      "========== CREATE ITEM =========="
    );

    console.log(
      "WORKSPACE UUID:",
      workspaceUuid
    );

    console.log(
      "PAYLOAD:",
      payload
    );

    const response =
      await createWorkspaceItem(
        workspaceUuid,
        payload
      );

    console.log(
      "CREATE ITEM SUCCESS:",
      response
    );

    /**
     * VERY IMPORTANT
     *
     * Tell WorkspaceDrawer that item
     * was successfully created.
     */

    if (onCreated) {
      await onCreated(
        response?.data || response
      );
    }

    /**
     * Reset form if required
     */

    setFormData({
      title: "",
      description: "",
      estimated_hours: "",
      priority: "Medium",
      status: "Planning",
    });

  } catch (error) {

    console.error(
      "CREATE ITEM ERROR:",
      error
    );

    setError(
      error.response?.data?.message ||
        "Failed to create item."
    );

  } finally {
    setLoading(false);
  }
};

  return (
    <>
      {/* =====================================================
          BACKDROP
      ===================================================== */}

      <div
        className="fixed inset-0 z-[90] bg-black/40"
        onClick={onClose}
      />

      {/* =====================================================
          DRAWER
      ===================================================== */}

      <div className="fixed right-0 top-0 z-[100] flex h-screen w-full flex-col border-l border-slate-200 bg-white shadow-2xl md:w-[70%] lg:w-1/2">

        {/* ===================================================
            HEADER
        =================================================== */}

        <div className="shrink-0 border-b border-slate-200 px-6 py-5">

          <div className="flex items-center justify-between">

            <div className="flex items-center gap-3">

              <div className="rounded-lg bg-blue-100 p-3">

                <ClipboardList
                  size={21}
                  className="text-blue-600"
                />

              </div>

              <div>

                <h2 className="text-xl font-bold text-slate-800">
                  Create Item
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Add an item to{" "}
                  {workspace?.name ||
                    "this workspace"}
                </p>

              </div>

            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-slate-100"
            >
              <X size={20} />
            </button>

          </div>

        </div>

        {/* ===================================================
            FORM
        =================================================== */}

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >

          <div className="min-h-0 flex-1 overflow-y-auto p-6">

            <div className="space-y-5">

              {/* ERROR */}

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* TITLE */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Item Title
                </label>

                <input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="API Development"
                  required
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-blue-500 focus:outline-none"
                />

              </div>

              {/* DESCRIPTION */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Description
                </label>

                <textarea
                  name="description"
                  value={
                    formData.description
                  }
                  onChange={handleChange}
                  rows={4}
                  placeholder="Develop and integrate project APIs."
                  className="mt-2 w-full resize-none rounded-lg border border-slate-300 px-4 py-3 focus:border-blue-500 focus:outline-none"
                />

              </div>

              {/* ESTIMATED HOURS */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Estimated Hours
                </label>

                <input
                  type="number"
                  name="estimated_hours"
                  value={
                    formData.estimated_hours
                  }
                  onChange={handleChange}
                  min="0"
                  placeholder="40"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-blue-500 focus:outline-none"
                />

              </div>

              {/* PRIORITY + STATUS */}

              <div className="grid grid-cols-2 gap-4">

                {/* PRIORITY */}

                <div>

                  <label className="text-sm font-medium text-slate-700">
                    Priority
                  </label>

                  <select
                    name="priority"
                    value={
                      formData.priority
                    }
                    onChange={handleChange}
                    className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5"
                  >

                    <option value="Critical">
                      Critical
                    </option>

                    <option value="High">
                      High
                    </option>

                    <option value="Medium">
                      Medium
                    </option>

                    <option value="Low">
                      Low
                    </option>

                  </select>

                </div>

                {/* STATUS */}

                <div>

                  <label className="text-sm font-medium text-slate-700">
                    Status
                  </label>

                  <select
                    name="status"
                    value={
                      formData.status
                    }
                    onChange={handleChange}
                    className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5"
                  >

                    <option value="Active">
                      Active
                    </option>

                    <option value="Planning">
                      Planning
                    </option>

                    <option value="Completed">
                      Completed
                    </option>

                    <option value="On Hold">
                      On Hold
                    </option>

                  </select>

                </div>

              </div>

              {/* SORT ORDER */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Sort Order
                </label>

                <input
                  type="number"
                  name="sort_order"
                  value={
                    formData.sort_order
                  }
                  onChange={handleChange}
                  min="1"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5"
                />

              </div>

            </div>

          </div>

          {/* =================================================
              FOOTER
          ================================================= */}

          <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-4">

            <div className="flex justify-end gap-3">

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >

                {loading && (
                  <Loader2
                    size={17}
                    className="animate-spin"
                  />
                )}

                {loading
                  ? "Creating..."
                  : "Create Item"}

              </button>

            </div>

          </div>

        </form>

      </div>
    </>
  );
};

export default AssignTaskDrawer;